import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { get, post } from '../../../api/apiClient';

export function mqttTabVisible(role) {
  return String(role || '').toLowerCase() === 'superadmin';
}

const ACCESS_FILTERS = [
  ['', 'Any access'],
  ['unprovisioned', 'Not provisioned'],
  ['provisioning', 'Provisioning'],
  ['ready', 'Provisioned'],
  ['failed', 'Needs attention'],
  ['revoking', 'Revoking access'],
  ['revoked', 'Access revoked'],
];

const CONNECTION_FILTERS = [
  ['', 'Any connection'],
  ['connected', 'Connected'],
  ['disconnected', 'Disconnected'],
  ['shared', 'Shared login — migration required'],
  ['unconfirmed', 'Unknown'],
  ['unknown', 'Not checked'],
  ['stale', 'Last checked'],
  ['unavailable', 'Unavailable'],
];

export function formatWhen(value, now = Date.now()) {
  if (value == null || value === '') return { label: '—', exact: '' };
  const numeric = typeof value === 'number' ? value : Number(value);
  const parsed = value instanceof Date
    ? value
    : new Date(Number.isFinite(numeric) && String(value).trim() !== '' && Math.abs(numeric) < 1e12 ? numeric * 1000 : value);
  if (Number.isNaN(parsed.getTime())) return { label: 'Unavailable', exact: String(value) };
  const exact = parsed.toLocaleString();
  const delta = parsed.getTime() - now;
  if (delta > 60 * 1000) return { label: 'In the future', exact };
  const elapsed = Math.max(0, -delta);
  if (elapsed < 45 * 1000) return { label: 'Just now', exact };
  const minutes = Math.round(elapsed / 60000);
  if (minutes < 60) return { label: `${minutes} minute${minutes === 1 ? '' : 's'} ago`, exact };
  const hours = Math.round(elapsed / 3600000);
  if (hours < 36) return { label: `${hours} hour${hours === 1 ? '' : 's'} ago`, exact };
  const days = Math.round(elapsed / 86400000);
  return { label: `${days} day${days === 1 ? '' : 's'} ago`, exact };
}

export function accessLabel(item) {
  const state = item?.provisioningState || 'unprovisioned';
  if (state === 'unprovisioned' || state === 'none' || state === 'pending') {
    return { label: 'Not provisioned', reason: '' };
  }
  if (state === 'provisioning') return { label: 'Provisioning', reason: '' };
  if (state === 'revoking' && item?.error) {
    return { label: 'Needs attention', reason: item.error };
  }
  if (state === 'revoking') return { label: 'Revoking access', reason: '' };
  if (state === 'failed') return { label: 'Needs attention', reason: item?.error || 'Provisioning failed' };
  if (state === 'revoked') return { label: 'Access revoked', reason: '' };
  if (state === 'ready') {
    return {
      label: 'Provisioned',
      reason: item?.rulesMatch === false ? 'Topic permissions differ from the intended rules' : '',
    };
  }
  return { label: 'Needs attention', reason: item?.error || 'The broker access state is not recognized' };
}

export function connectionLabel(item, now = Date.now()) {
  const state = item?.connectionState || 'unknown';
  if (state === 'unavailable') return { label: 'Unavailable', detail: 'The last broker check did not succeed.' };
  if (state === 'shared') {
    const when = item?.connectionObservedAt ? formatWhen(item.connectionObservedAt, now) : null;
    const checked = when && when.label !== '—' ? `Last checked ${when.label}. ` : '';
    return {
      label: 'Shared login — migration required',
      detail: `${checked}This Runtime is signed in with the shared broker login.`,
    };
  }
  if (state === 'unconfirmed') {
    return { label: 'Unknown', detail: 'No individual or shared broker session was found for this Agent.' };
  }
  if (state === 'unknown' || !item?.connectionObservedAt) return { label: 'Not checked', detail: '' };
  const when = formatWhen(item.connectionObservedAt, now);
  if (when.label === 'Unavailable' || when.label === 'In the future') {
    return { label: when.label, detail: '' };
  }
  if (state === 'stale') return { label: `Last checked ${when.label}`, detail: 'This broker check is out of date.' };
  if (state === 'connected') return { label: 'Connected', detail: `Last checked ${when.label}` };
  if (state === 'disconnected') return { label: 'Disconnected', detail: `Last checked ${when.label}` };
  return { label: 'Not checked', detail: '' };
}

function actionsFor(item) {
  const state = item?.provisioningState || 'unprovisioned';
  const actions = [];
  if (state === 'revoking' || item?.cleanupPending) actions.push({ id: 'retry', label: 'Retry cleanup' });
  else if (state === 'unprovisioned' || state === 'pending' || state === 'failed' || item?.retryable) {
    actions.push({ id: 'retry', label: 'Retry provisioning' });
  }
  if (item?.connectionState === 'connected' || Number(item?.connectedCount) > 0) {
    actions.push({ id: 'disconnect', label: 'Disconnect', confirm: true });
  }
  if (state !== 'revoked' && state !== 'revoking') actions.push({ id: 'revoke', label: 'Revoke access', confirm: true });
  if (state === 'revoked') actions.push({ id: 'restore', label: 'Restore access', confirm: true });
  return actions;
}

function confirmCopy(item, action) {
  const name = item?.name || 'This Agent';
  const tenant = item?.tenantName || item?.tenantId || 'its tenant';
  if (action === 'disconnect') {
    return `Disconnect ${name} for ${tenant}? This closes its current broker sessions. Disconnect allows the Runtime to reconnect with its current login.`;
  }
  if (action === 'revoke') {
    return `Revoke access for ${name} at ${tenant}? This removes that Agent’s broker login and closes its sessions. Revoke does not allow reconnection until access is restored.`;
  }
  if (action === 'restore') {
    return `Restore access for ${name} at ${tenant}? This creates a new broker password. The previous password stays invalid, and the Runtime needs the new login.`;
  }
  return '';
}

function ruleLines(rules) {
  if (!Array.isArray(rules) || rules.length === 0) return 'None recorded';
  return rules.map((rule) => {
    const topic = rule?.topic || 'unknown topic';
    const permission = rule?.permission || 'unknown';
    const action = rule?.action || 'all';
    return `${permission} ${action} ${topic}`;
  }).join('\n');
}

function CopyLine({ label, value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <Typography variant="body2">{label}: —</Typography>;
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="body2">{label}: {value}</Typography>
      <Button
        size="small"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(String(value));
            setCopied(true);
          } catch {
            setCopied(false);
          }
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </Stack>
  );
}

export function AgentMqttTable({ data, error, busy, loading, filters, onFilters, onAction, onRefresh }) {
  const [pending, setPending] = useState(null);
  const [expanded, setExpanded] = useState('');
  const [menu, setMenu] = useState(null);
  const items = data?.items || [];
  const totalKnown = Number.isInteger(data?.total);
  const total = totalKnown ? data.total : null;
  const limit = data?.limit || filters.limit || 25;
  const offset = data?.offset || 0;
  const hasMore = data?.hasMore === true;
  const nextDisabled = hasMore ? false : (totalKnown ? offset + limit >= total : true);
  const rangeStart = items.length ? offset + 1 : 0;
  const rangeEnd = offset + items.length;
  const rangeLabel = totalKnown ? `${rangeStart}–${rangeEnd} of ${total}` : (items.length ? `${rangeStart}–${rangeEnd}` : '0');
  const tenants = data?.tenants || [];
  const menuItem = items.find((item) => item.agentId === menu?.agentId);

  function setFilter(patch) {
    onFilters({ ...filters, ...patch, offset: patch.offset ?? 0 });
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>MQTT</Typography>
      <Button size="small" onClick={onRefresh} disabled={busy || loading} sx={{ mb: 1 }}>Refresh</Button>
      {loading ? <Typography variant="body2">Loading MQTT inventory</Typography> : null}
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {data?.brokerUnavailable ? <Alert severity="warning" sx={{ mb: 2 }}>Broker observations are unavailable.</Alert> : null}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 2 }} alignItems={{ md: 'center' }}>
        <TextField size="small" label="Search" value={filters.q} onChange={(event) => setFilter({ q: event.target.value })} />
        <TextField
          select
          size="small"
          label="Tenant"
          value={filters.tenantId}
          onChange={(event) => setFilter({ tenantId: event.target.value })}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All tenants</MenuItem>
          {tenants.map((tenant) => (
            <MenuItem key={tenant.tenantId} value={tenant.tenantId}>{tenant.tenantName || tenant.tenantId}</MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Broker access" value={filters.provisioning} onChange={(event) => setFilter({ provisioning: event.target.value })} sx={{ minWidth: 170 }}>
          {ACCESS_FILTERS.map(([value, label]) => <MenuItem key={value || 'any-access'} value={value}>{label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="MQTT connection" value={filters.connection} onChange={(event) => setFilter({ connection: event.target.value })} sx={{ minWidth: 170 }}>
          {CONNECTION_FILTERS.map(([value, label]) => <MenuItem key={value || 'any-connection'} value={value}>{label}</MenuItem>)}
        </TextField>
        <FormControlLabel
          control={(
            <Checkbox
              checked={Boolean(filters.includeHistorical)}
              onChange={(event) => setFilter({ includeHistorical: event.target.checked })}
            />
          )}
          label="Include inactive and historical records"
        />
      </Stack>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Agent</TableCell>
            <TableCell>Tenant</TableCell>
            <TableCell>
              <Tooltip title="This is the broker session. It does not show whether paging is ready.">
                <span>MQTT connection</span>
              </Tooltip>
            </TableCell>
            <TableCell>Broker access</TableCell>
            <TableCell>Last seen</TableCell>
            <TableCell>Manage</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => {
            const access = accessLabel(item);
            const connection = connectionLabel(item);
            const seen = formatWhen(item.heartbeatAt);
            const open = expanded === item.agentId;
            return (
              <React.Fragment key={item.agentId}>
                <TableRow>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.tenantName || '—'}</TableCell>
                  <TableCell>
                    <Tooltip title={connection.detail || 'No broker check yet'}>
                      <span>{connection.label}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={access.reason || access.label}>
                      <Chip size="small" label={access.label} />
                    </Tooltip>
                    {access.reason ? <Typography variant="caption" display="block">{access.reason}</Typography> : null}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={seen.exact || 'No heartbeat has been recorded'}>
                      <span>{seen.label}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      aria-label={`Manage ${item.name}`}
                      onClick={(event) => setMenu({ anchor: event.currentTarget, agentId: item.agentId })}
                    >
                      Manage
                    </Button>
                    <Button size="small" onClick={() => setExpanded(open ? '' : item.agentId)}>
                      {open ? 'Hide details' : 'Details'}
                    </Button>
                  </TableCell>
                </TableRow>
                {open ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Stack spacing={0.5}>
                        <CopyLine label="Agent ID" value={item.agentId} />
                        <CopyLine label="MQTT username" value={item.username} />
                        <Typography variant="body2">Tenant ID: {item.tenantId || '—'}</Typography>
                        <Typography variant="body2">
                          Client sessions: {(item.connectedClientIds || []).join(', ') || 'None'}
                          {item.connectionObservedAt ? ` · Observed ${formatWhen(item.connectionObservedAt).exact || formatWhen(item.connectionObservedAt).label}` : ' · Not checked'}
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>Intended permissions: {ruleLines(item.intendedRules)}</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>Observed permissions: {ruleLines(item.observedRules)}</Typography>
                        <Typography variant="body2">
                          {item.generation ? `Credential generation ${item.generation}` : 'No credential generated'}
                        </Typography>
                        <Typography variant="body2">
                          Last provisioning attempt: {formatWhen(item.lastAttemptAt).exact || formatWhen(item.lastAttemptAt).label}
                          {item.error ? ` · ${item.error}` : ''}
                        </Typography>
                        {item.cleanupPending ? (
                          <Typography variant="body2">Broker cleanup is still pending and can be retried.</Typography>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : null}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
      <Stack direction="row" spacing={1} sx={{ mt: 2 }} alignItems="center">
        <Button size="small" disabled={offset <= 0 || busy} onClick={() => onFilters({ ...filters, offset: Math.max(0, offset - limit) })}>Previous</Button>
        <Typography variant="body2">{rangeLabel}</Typography>
        <Button size="small" disabled={nextDisabled || busy} onClick={() => onFilters({ ...filters, offset: offset + limit })}>Next</Button>
      </Stack>
      <Menu
        anchorEl={menu?.anchor || null}
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
      >
        {(menuItem ? actionsFor(menuItem) : []).map((action) => (
          <MenuItem
            key={action.id}
            onClick={() => {
              const current = menuItem;
              setMenu(null);
              if (action.confirm) setPending({ item: current, action: action.id });
              else onAction(current.agentId, action.id);
            }}
          >
            {action.label}
          </MenuItem>
        ))}
      </Menu>
      <Dialog open={Boolean(pending)} onClose={() => setPending(null)}>
        <DialogTitle>Confirm {pending?.action}</DialogTitle>
        <DialogContent>
          <DialogContentText>{pending ? confirmCopy(pending.item, pending.action) : ''}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)}>Cancel</Button>
          <Button onClick={() => { const action = pending; setPending(null); onAction(action.item.agentId, action.action); }}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default function AgentMqttTab() {
  const [filters, setFilters] = useState({
    q: '', tenantId: '', provisioning: '', connection: '', includeHistorical: false, offset: 0, limit: 25,
  });
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (refresh = false) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'includeHistorical') {
        if (value) params.set('includeHistorical', '1');
        return;
      }
      if (value !== '' && value != null) params.set(key, String(value));
    });
    if (refresh) params.set('refresh', '1');
    setLoading(true);
    try {
      const response = await get(`/api/v2/admin/agents/mqtt?${params.toString()}`);
      const page = response?.data || response;
      setData(page);
      setError('');
    } catch (err) {
      setError(err?.message || 'MQTT inventory is unavailable');
      setData((current) => (current ? { ...current, brokerUnavailable: true } : current));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  async function onAction(agentId, action) {
    setBusy(true);
    setError('');
    try {
      await post(`/api/v2/admin/agents/${encodeURIComponent(agentId)}/mqtt/${action}`, {});
      await load(false);
    } catch (err) {
      setError(err?.message || `${action} failed`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AgentMqttTable
      data={data}
      error={error}
      busy={busy}
      loading={loading}
      filters={filters}
      onFilters={setFilters}
      onAction={onAction}
      onRefresh={() => load(true)}
    />
  );
}
